/**
 * Dialog layer for Team Admin → Identity Providers.
 *
 * Split out of `identity-providers.tsx` to keep both files inside the 300-line
 * ceiling. Holds no data of its own: the page owns which dialog is open and
 * every mutation; this file owns the copy, the confirmation consequences and
 * the persona checkbox grid.
 */
import { ConfirmDialog } from '../../components/confirm-dialog';
import { OverlayDialog } from '../../components/overlay-dialog';
import { PERSONA_LABELS, type Persona } from '../../lib/personas';

import { CopyField } from './federation-endpoints';
import { IdentityProviderForm, type ProviderFormValues } from './identity-provider-form';
import { CONFIGURABLE_PERSONAS, type IdentityProviderView } from './use-identity-providers';

export type OpenDialog =
  | { kind: 'none' }
  | { kind: 'create' }
  | { kind: 'edit'; provider: IdentityProviderView }
  | { kind: 'policy'; provider: IdentityProviderView }
  | { kind: 'disable'; provider: IdentityProviderView }
  | { kind: 'rotate'; provider: IdentityProviderView }
  | { kind: 'delete'; provider: IdentityProviderView }
  | { kind: 'token'; provider: IdentityProviderView; token: string };

export interface ProviderDialogsProps {
  dialog: OpenDialog;
  error: string | null;
  draftPersonas: readonly Persona[];
  onDraftPersonasChange: (personas: Persona[]) => void;
  onClose: () => void;
  onSubmitProvider: (values: ProviderFormValues, existingId?: string) => Promise<void>;
  onSavePolicy: (provider: IdentityProviderView) => Promise<void>;
  onDisable: (provider: IdentityProviderView) => Promise<void>;
  onRotate: (provider: IdentityProviderView) => Promise<void>;
  onDelete: (provider: IdentityProviderView) => Promise<void>;
}

function policyConsequence(personas: readonly Persona[]): string {
  if (personas.length === 0) {
    return 'Nobody will be able to sign in through this provider until you allow at least one persona.';
  }
  const names = personas.map((persona) => PERSONA_LABELS[persona]).join(', ');
  return `Only ${names} will be able to sign in through this provider. Everyone else is refused at login.`;
}

export function ProviderDialogs(props: ProviderDialogsProps): JSX.Element | null {
  const { dialog, error, draftPersonas, onClose } = props;

  if (dialog.kind === 'none') return null;

  if (dialog.kind === 'create' || dialog.kind === 'edit') {
    const provider = dialog.kind === 'edit' ? dialog.provider : undefined;
    return (
      <OverlayDialog
        title={provider ? `Edit ${provider.display_name}` : 'Add Identity Provider'}
        onClose={onClose}
        panelClassName="federation-dialog"
      >
        <IdentityProviderForm
          provider={provider}
          onCancel={onClose}
          onSubmit={(values) => props.onSubmitProvider(values, provider?.provider_id)}
        />
      </OverlayDialog>
    );
  }

  if (dialog.kind === 'policy') {
    const provider = dialog.provider;
    return (
      <ConfirmDialog
        title={`Allowed personas — ${provider.display_name}`}
        consequence={policyConsequence(draftPersonas)}
        confirmLabel="Save policy"
        error={error}
        onCancel={onClose}
        onConfirm={() => props.onSavePolicy(provider)}
      >
        <div className="persona-grid">
          {CONFIGURABLE_PERSONAS.map((persona) => (
            <label key={persona} className="checkbox checkbox--compact">
              <input
                type="checkbox"
                checked={draftPersonas.includes(persona)}
                onChange={(event) =>
                  props.onDraftPersonasChange(
                    event.target.checked
                      ? [...draftPersonas, persona]
                      : draftPersonas.filter((entry) => entry !== persona)
                  )
                }
              />
              <span>{PERSONA_LABELS[persona]}</span>
            </label>
          ))}
        </div>
      </ConfirmDialog>
    );
  }

  if (dialog.kind === 'disable') {
    const provider = dialog.provider;
    return (
      <ConfirmDialog
        title={`Disable ${provider.display_name}?`}
        tone="danger"
        consequence={
          provider.protocol === 'saml2'
            ? 'Staff will no longer be able to sign in with your school account through this provider. Existing sessions stay active until they expire.'
            : 'Your directory will stop being able to create, update or de-provision users. Accounts already provisioned keep their access.'
        }
        confirmLabel="Disable provider"
        error={error}
        onCancel={onClose}
        onConfirm={() => props.onDisable(provider)}
      />
    );
  }

  if (dialog.kind === 'rotate') {
    const provider = dialog.provider;
    return (
      <ConfirmDialog
        title={`Rotate the SCIM token for ${provider.display_name}?`}
        tone="danger"
        consequence="The current token stops working immediately. Directory sync fails until you paste the new token into your IdP."
        confirmLabel="Rotate token"
        error={error}
        onCancel={onClose}
        onConfirm={() => props.onRotate(provider)}
      />
    );
  }

  if (dialog.kind === 'delete') {
    const provider = dialog.provider;
    return (
      <ConfirmDialog
        title={`Delete ${provider.display_name}?`}
        tone="danger"
        consequence="The provider, its persona policy and its SCIM token are removed permanently. Users it provisioned keep their accounts, but the directory can no longer manage them."
        confirmLabel="Delete provider"
        error={error}
        onCancel={onClose}
        onConfirm={() => props.onDelete(provider)}
      />
    );
  }

  return (
    <OverlayDialog title="Copy your SCIM bearer token" onClose={onClose}>
      <div className="stack">
        <p>
          This token is shown once. Paste it into the SCIM connector of{' '}
          <strong>{dialog.provider.display_name}</strong> together with the SCIM base URL on the
          page behind this dialog.
        </p>
        <CopyField label="Bearer token" value={dialog.token} isSecret />
        <div className="btn-row btn-row--end">
          <button type="button" className="btn btn--primary" onClick={onClose}>
            I have saved it
          </button>
        </div>
      </div>
    </OverlayDialog>
  );
}
